import React, { useState, useEffect, useCallback } from 'react';
import { productService, extractErrorDetails } from '../services/api';
import BarcodeScanner from './BarcodeScanner';
import ErrorDisplay from './ErrorDisplay';

function StockManagement() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockAdjustment, setStockAdjustment] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [adjusting, setAdjusting] = useState(false);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [success, setSuccess] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [stockMinimo, setStockMinimo] = useState('');

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      const response = await productService.getAllProducts();
      if (response.success) {
        setProducts(response.data || []);
      } else {
        setError(response.message || 'Error al cargar los productos');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar los productos');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredProducts([]);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = products.filter(product =>
        product.codigoBarras.toLowerCase().includes(query) ||
        product.nombre.toLowerCase().includes(query)
      );
      setFilteredProducts(filtered.slice(0, 10));
    }
  }, [searchQuery, products]);

  const handleScan = async (barcode) => {
    setShowScanner(false);
    setSearchQuery(barcode);
    setError('');
    setErrorTrace(null);
    
    try {
      const response = await productService.getProductByBarcode(barcode);
      if (response.success && response.data) {
        setSelectedProduct(response.data);
        setSearchQuery('');
        setFilteredProducts([]);
      } else {
        setError(response.message || 'Producto no encontrado con ese código de barras');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Producto no encontrado con ese código de barras');
      setError(message);
      setErrorTrace(trace);
    }
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);
    setSearchQuery('');
    setFilteredProducts([]);
    setStockAdjustment('');
    setStockMinimo(product.stockMinimo != null ? product.stockMinimo.toString() : '0');
    setError('');
    setErrorTrace(null);
    setSuccess('');
  };

  const handleAdjustStock = async (e) => {
    e.preventDefault();
    
    if (!selectedProduct || !stockAdjustment) {
      setError('Debe seleccionar un producto e ingresar una cantidad');
      return;
    }

    const cantidad = parseInt(stockAdjustment);
    if (isNaN(cantidad) || cantidad === 0) {
      setError('La cantidad debe ser un número diferente de cero');
      return;
    }

    setAdjusting(true);
    setError('');
    setErrorTrace(null);
    setSuccess('');

    try {
      const response = await productService.adjustStock(selectedProduct.id, cantidad);
      if (response.success) {
        setSuccess(`Stock ajustado exitosamente. Nuevo stock: ${response.data.stock}`);
        setSelectedProduct(response.data);
        setStockAdjustment('');
        // Update the product in the list
        setProducts(prev => prev.map(p => 
          p.id === response.data.id ? response.data : p
        ));
      } else {
        setError(response.message || 'Error al ajustar el stock');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al ajustar el stock');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setAdjusting(false);
    }
  };

  const clearSelection = () => {
    setSelectedProduct(null);
    setStockAdjustment('');
    setStockMinimo('');
    setError('');
    setErrorTrace(null);
    setSuccess('');
  };
  
  const handleUpdateMinimumStock = async (e) => {
    e.preventDefault();
    
    if (!selectedProduct || stockMinimo === '') {
      setError('Debe ingresar un valor para el stock mínimo');
      return;
    }

    const minStock = parseInt(stockMinimo);
    if (isNaN(minStock) || minStock < 0) {
      setError('El stock mínimo debe ser un número mayor o igual a cero');
      return;
    }

    setAdjusting(true);
    setError('');
    setErrorTrace(null);
    setSuccess('');

    try {
      const response = await productService.updateMinimumStock(selectedProduct.id, minStock);
      if (response.success) {
        setSuccess(`Stock mínimo actualizado exitosamente a ${minStock}`);
        setSelectedProduct(response.data);
        // Update the product in the list
        setProducts(prev => prev.map(p => 
          p.id === response.data.id ? response.data : p
        ));
      } else {
        setError(response.message || 'Error al actualizar el stock mínimo');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al actualizar el stock mínimo');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setAdjusting(false);
    }
  };

  return (
    <div className="stock-management">
      <h2>Gestión de Stock</h2>
      
      {showScanner && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      <div className="stock-layout">
        {/* Product Search Panel */}
        <div className="search-panel">
          <h3>Buscar Producto</h3>
          <div className="search-input-container">
            <div className="input-with-button">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por código o nombre..."
                className="search-input"
              />
              <button 
                type="button" 
                onClick={() => setShowScanner(true)}
                className="btn btn-icon"
                title="Escanear código de barras"
              >
                📷
              </button>
            </div>
          </div>

          {loading ? (
            <p className="loading-text">Cargando productos...</p>
          ) : (
            <>
              {filteredProducts.length > 0 && (
                <div className="product-results">
                  <h4>Resultados ({filteredProducts.length})</h4>
                  <ul className="product-list">
                    {filteredProducts.map(product => (
                      <li 
                        key={product.id}
                        onClick={() => selectProduct(product)}
                        className="product-item"
                      >
                        <span className="product-code">{product.codigoBarras}</span>
                        <span className="product-name">{product.nombre}</span>
                        <span className={`stock-info ${product.stock > 0 ? 'positive' : product.stock < 0 ? 'negative' : 'zero'}`}>
                          Stock: {product.stock}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {searchQuery.length >= 2 && filteredProducts.length === 0 && (
                <p className="no-results">No se encontraron productos</p>
              )}

              {searchQuery.length === 0 && !selectedProduct && (
                <p className="search-hint">Escriba al menos 2 caracteres o escanee un código de barras</p>
              )}
            </>
          )}
        </div>

        {/* Stock Adjustment Panel */}
        <div className="adjustment-panel">
          <h3>Ajustar Stock</h3>
          
          {selectedProduct ? (
            <div className="selected-product-card">
              <div className="product-info">
                <div className="product-header">
                  <h4>{selectedProduct.nombre}</h4>
                  <button 
                    type="button" 
                    onClick={clearSelection}
                    className="btn btn-small btn-secondary"
                  >
                    ✕ Cambiar
                  </button>
                </div>
                <p className="product-barcode">Código de Barras: {selectedProduct.codigoBarras}</p>
                {selectedProduct.codigoExterno && (
                  <p className="product-external-code">Código Externo: {selectedProduct.codigoExterno}</p>
                )}
                <p className="current-stock">
                  Stock actual: 
                  <span className={`stock-value ${selectedProduct.stock > 0 ? 'positive' : selectedProduct.stock < 0 ? 'negative' : 'zero'}`}>
                    {selectedProduct.stock}
                  </span>
                </p>
                <p className="minimum-stock">
                  Stock mínimo: 
                  <span className="stock-value">
                    {selectedProduct.stockMinimo != null ? selectedProduct.stockMinimo : 0}
                  </span>
                </p>
              </div>

              <form onSubmit={handleAdjustStock} className="adjustment-form">
                <div className="input-group">
                  <label htmlFor="stockAdjustment">Cantidad a agregar/quitar</label>
                  <input
                    type="number"
                    id="stockAdjustment"
                    value={stockAdjustment}
                    onChange={(e) => setStockAdjustment(e.target.value)}
                    placeholder="Ej: 10 o -5"
                    className="stock-input"
                    autoFocus
                  />
                  <small className="input-hint">
                    Use números positivos para agregar y negativos para quitar
                  </small>
                </div>

                <ErrorDisplay error={error} errorTrace={errorTrace} />
                {success && <p className="success-message">{success}</p>}

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={adjusting || !stockAdjustment}
                  >
                    {adjusting ? 'Ajustando...' : 'Ajustar Stock'}
                  </button>
                </div>
              </form>
              
              <form onSubmit={handleUpdateMinimumStock} className="adjustment-form" style={{marginTop: '20px'}}>
                <div className="input-group">
                  <label htmlFor="stockMinimo">Stock Mínimo</label>
                  <input
                    type="number"
                    id="stockMinimo"
                    value={stockMinimo}
                    onChange={(e) => setStockMinimo(e.target.value)}
                    placeholder="Ej: 10"
                    className="stock-input"
                    min="0"
                  />
                  <small className="input-hint">
                    Define el nivel mínimo de stock para este producto
                  </small>
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-secondary"
                    disabled={adjusting || stockMinimo === ''}
                  >
                    {adjusting ? 'Actualizando...' : 'Actualizar Stock Mínimo'}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="no-selection">
              <p>Seleccione un producto para ajustar su stock</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StockManagement;
